import Vue from 'vue'
import Vuex, { ActionContext } from 'vuex'
import * as api from '@immosparrow/cockpit-api-v2'
import { CustomerLeadEmployee, CustomerLeadsApi } from '@immosparrow/cockpit-middleware-client'

Vue.use(Vuex)

class Gallery {
  index!: number | null;
  pictures!: string[];
}

class FeedbackData {
  lead!: api.CustomerLeadLightModel | null;
  status!: boolean | null;
}

class State {
  language!: string;
  lead!: api.CustomerLeadLightModel | null;
  multipleLeads!: api.CustomerLeadLightModel[];
  multipleLeadsIndex!: number;
  propertyTypes!: api.IPubPropertyCategory[];
  gallery!: Gallery;
  agencyTheme!: string;
  lastMailInfo!: api.ICustomerLeadMailModel | null;
  mailInfo!: api.ICustomerLeadMailModel | null;
  agentInfo!: CustomerLeadEmployee | null;
  contactInfo!: api.IContactModel | null;
  subscribed!: boolean;
  feedbackData!: FeedbackData;
}

const state: State = {
  language: 'en',
  lead: null,
  multipleLeads: [],
  multipleLeadsIndex: 0,
  propertyTypes: [],
  gallery: {
    index: null,
    pictures: []
  },
  agencyTheme: '',
  mailInfo: null,
  lastMailInfo: null,
  agentInfo: null,
  contactInfo: null,
  subscribed: false,
  feedbackData: {
    lead: null,
    status: null
  }
}

const store = new Vuex.Store({
  state,
  getters: {
    propertyTypes: (state: State) => {
      return state.propertyTypes
    },
    language: (state: State) => {
      return state.language
    },
    lead: (state: State) => {
      return state.lead
    },
    multipleLeads: (state: State) => {
      return state.multipleLeads
    },
    multipleLeadsIndex: (state: State) => {
      return state.multipleLeadsIndex
    },
    gallery: (state: State) => {
      return state.gallery
    },
    agencyTheme: (state: State) => {
      return state.agencyTheme
    },
    mailInfo: (state: State) => {
      return state.mailInfo
    },
    lastMailInfo: (state: State) => {
      return state.lastMailInfo
    },
    agentInfo: (state: State) => {
      return state.agentInfo
    },
    contactInfo: (state: State) => {
      return state.contactInfo
    },
    subscribed: (state: State) => {
      return state.subscribed
    },
    feedbackData: (state: State) => {
      return state.feedbackData
    }
  },
  mutations: {
    setLead (state: State, lead: api.CustomerLeadLightModel | null) {
      state.lead = lead
    },
    setMultipleLeads (state: State, leads: api.CustomerLeadLightModel[]) {
      state.multipleLeads = leads
    },
    setMultipleLeadsIndex (state: State, index: number) {
      state.multipleLeadsIndex = index
    },
    setLanguage (state: State, lang: string) {
      state.language = lang
      window['Localize'].setLanguage(lang)
    },
    setPropertyTypes (state: State, propertyTypes: api.IPubPropertyCategory[]) {
      state.propertyTypes = propertyTypes
    },
    setGallery (state: State, gallery: Gallery) {
      state.gallery = gallery
    },
    setGalleryIndex (state: State, index: number) {
      state.gallery.index = index
    },
    setAgencyTheme (state: State, agencyTheme: string) {
      state.agencyTheme = agencyTheme
    },
    setMailInfo (state: State, mailInfo: api.ICustomerLeadMailModel) {
      state.mailInfo = mailInfo
    },
    setLastMailInfo (state: State, lastMailInfo: api.ICustomerLeadMailModel) {
      state.lastMailInfo = lastMailInfo
    },
    setAgentInfo (state: State, agentInfo: api.ICustomerLeadEmployee) {
      state.agentInfo = agentInfo
    },
    setContactInfo (state: State, contactInfo: api.IContactModel) {
      state.contactInfo = contactInfo
    },
    setSubscribed (state: State, subscribed: boolean) {
      state.subscribed = subscribed
    },
    setFeedbackData (state: State, feedbackData: FeedbackData) {
      state.feedbackData = feedbackData
    }
  },
  actions: {
    async loadLeads (context: ActionContext<State, any>, data: { id: string; query: api.CustomerLeadSearchQuery }) {
      try {
        return await api.$leadMails.findLeads(data.id, data.query)
      } catch {
        return false
      }
    },
    async updateLeadStatus (context: ActionContext<State, any>, feedback: api.CustomerLeadFeedbackModel) {
      return await api.$leadMails.updateFeedback([feedback])
    },
    async getPropertyCategories (context: ActionContext<State, any>) {
      const categories = await api.$leadMails.getPropertyCategories()
      context.commit('setPropertyTypes', categories)
    },
    async getMailInfo (context: ActionContext<State, any>, mailId: string) {
      const mailInfo = await api.$leadMails.getMailInfoMultiple([mailId])
      context.commit('setMailInfo', mailInfo)
    },
    async loadAgentInfo (context: ActionContext<State, any>, id: string) {
      const authUserApi = new CustomerLeadsApi()
      const response = await authUserApi.apiV20CustomerLeadsEmployeeInfoIdGet(id)
      context.commit('setAgentInfo', response.data)
      return response.data
    },
    async loadContactInfo (context: ActionContext<State, any>, id: string) {
      const contactInfo = await api.$leadMails.getContactInfo(id) || api.$newObj(api.ContactModel)
      context.commit('setContactInfo', contactInfo)
      return contactInfo
    },
    async getLeadById (context: ActionContext<State, any>, leadId: string) {
      const lead = await api.$leadMails.getLeadInfo(leadId)
      context.commit('setLead', lead)
      if (lead.mailId) context.dispatch('getMailInfo', lead.mailId)
      else context.commit('setMailInfo', null)
      return lead
    },
    async updateViewedStatus (context: ActionContext<State, any>, id: string) {
      return await api.$leadMails.viewLeads([id])
    },
    async unsubscribe (context: ActionContext<State, any>, id: string) {
      return await api.$leadMails.unsubscribe(id)
    },
    async getSubscribtionInfo (context: ActionContext<State, any>, id: string) {
      const info = await api.$leadMails.getSubscriptionInfo(id)
      context.commit('setSubscribed', info?.isEnabled || false)
    }
  }
})

export default store
